import {ComponentType, FC, useEffect} from 'react';
import {useAuth} from "../lib/auth.ts";
import LoadingScreen from "@memaro/components/LoadingScreen.tsx";
import {useRouter} from "../hooks/useRouter.ts";

export function authenticated<P extends object>(
    WrappedComponent: ComponentType<P>
): FC<P> {
    const AuthenticatedComponent: FC<P> = (props) => {
        const { state } = useAuth();
        const {gotoLogin} = useRouter()

        useEffect(() => {
            if (state != "initialising" && state != "authenticated") {
                gotoLogin()
            }
        }, [state, gotoLogin]);

        return state == "authenticated" ? <WrappedComponent {...props} /> : <LoadingScreen />;
    };

    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    AuthenticatedComponent.displayName = `authenticated(${displayName})`;

    return AuthenticatedComponent;
}

export function unauthenticated<P extends object>(
    WrappedComponent: ComponentType<P>
): FC<P> {
    const UnauthenticatedComponent: FC<P> = (props) => {
        const {state} = useAuth()
        const {gotoHome, gotoChangePassword} = useRouter()

        useEffect(() => {
            switch (state) {
                case "authenticated":
                    gotoHome()
                    break
                case "confirm_sign_in_with_new_password":
                    gotoChangePassword()
                    break
            }
        }, [gotoChangePassword, gotoHome, state])

        return state == "initialising" ? <LoadingScreen /> : <WrappedComponent {...props} />;
    };

    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    UnauthenticatedComponent.displayName = `unauthenticated(${displayName})`;

    return UnauthenticatedComponent;
}


export function resetPassword<P extends object>(
    WrappedComponent: ComponentType<P>
): FC<P> {
    const ResetPasswordComponent: FC<P> = (props) => {
        const { state } = useAuth();
        const {gotoLogin} = useRouter()

        const validState =  state == "authenticated" || state == "confirm_sign_in_with_new_password"

        useEffect(() => {
            if (state != "initialising" && !validState) {
                gotoLogin()
            }
        }, [validState, gotoLogin, state]);

        return validState ? <WrappedComponent {...props} /> : <LoadingScreen />;
    };

    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ResetPasswordComponent.displayName = `resetPasword(${displayName})`;

    return ResetPasswordComponent;
}
